import { useLocation, useNavigate } from '@tanstack/react-router';
import { Widget } from './types';
import { Card } from 'primereact/card';

interface WidgetResultsProps {
  widgets: Widget[];
  onWidgetClick: (widget: Widget) => void;
}

export const WidgetResults = ({
  widgets,
  onWidgetClick,
}: WidgetResultsProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const handleWidgetClick = (widget: Widget) => {
    navigate({
      to: `/${location.pathname}`,
      state: {
        widgetId: widget.id,
      },
    });
    onWidgetClick(widget);
  };

  // If there are no widgets, don't render anything
  if (!widgets || widgets.length === 0) {
    return null;
  }

  const header = (url: string, alt: string) => (
    <div
      className="p-2"
      style={{
        backgroundColor: 'var(--bq-surface-elevated)',
        borderTopLeftRadius: '4px',
        borderTopRightRadius: '4px',
      }}
    >
      <img
        alt={alt}
        src={url}
        className="h-14rem w-full object-contain"
        style={{
          borderRadius: '4px',
        }}
      />
    </div>
  );

  return (
    <div className="w-full">
      <div className="flex justify-content-between align-items-center">
        <h3
          style={{
            textTransform: 'uppercase',
            margin: '20px 0 10px 0',
            color: 'var(--bq-color-info, #3B82F6)',
          }}
        >
          Widgets
        </h3>
      </div>
      <div className="grid">
        {widgets.map((widget) => (
          <div key={widget.id} className="col-12 md:col-3">
            <Card
              header={header(widget.imageUrl, widget.name)}
              className="cursor-pointer h-26rem"
              onClick={() => handleWidgetClick(widget)}
              title={widget.name}
              style={{
                backgroundColor: 'var(--bq-surface-card)',
                border: '1px solid var(--bq-border-muted)',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                const card = e.currentTarget;
                card.style.boxShadow =
                  '0 8px 20px rgba(15, 23, 42, 0.15), 0 4px 10px rgba(15, 23, 42, 0.08)';
                card.style.transform = 'translateY(-2px)';
                card.style.borderColor = 'var(--bq-color-info)';
              }}
              onMouseLeave={(e) => {
                const card = e.currentTarget;
                card.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                card.style.transform = 'translateY(0)';
                card.style.borderColor = 'var(--bq-border-muted)';
              }}
              pt={{
                root: {
                  style: {
                    backgroundColor: 'var(--bq-surface-card)',
                    border: '1px solid var(--bq-border-muted)',
                    borderRadius: '4px',
                  },
                },
                header: {
                  style: {
                    padding: 0,
                    backgroundColor: 'var(--bq-surface-elevated)',
                  },
                },
                title: {
                  style: {
                    fontSize: '1.2rem',
                    fontWeight: '600',
                    color: 'var(--bq-text-primary)',
                    marginBottom: '0.5rem',
                  },
                },
                body: {
                  style: {
                    padding: '1rem',
                    backgroundColor: 'var(--bq-surface-card)',
                  },
                },
                content: {
                  style: {
                    padding: 0,
                    margin: 0,
                  },
                },
              }}
            >
              <p
                className="m-0 text-sm"
                style={{
                  color: 'var(--bq-text-secondary)',
                  lineHeight: '1.5',
                }}
              >
                {widget.description}
              </p>
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
};
